import { useEffect, useState } from "react";
import { Button, FlexColBox, Form, Icon, Input } from "../../common/components";
import { AdminIcon, Box } from "../../common/components";
import { palette, spacing } from "../../styles/theme";
import { Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useThemeBreakpoints } from "../../utils/themeUtils";
import { useForm } from "../../common/hooks/useForm";
import { object, string } from "yup";
import { testEmail } from "../../utils/FormValidationsUtils";
import TeamService from "../../service/TeamService";
import { useTeam } from "../../context/Team";
import { AxiosError } from "axios";
import Message, { MessageTypes } from "../Message/Message";
import { useAuth } from "../../common/hooks/useAuth";

const AddCollaboratorControls = () => {
  const { t } = useTranslation();
  const { isAdmin } = useAuth();
  const [added, setAdded] = useState(false);
  const { teamId, refetchTeam } = useTeam();
  const { isMobile } = useThemeBreakpoints();
  const [error, setError] = useState<string | null>(null);

  const onAdd = async () => {
    if (!teamId) {
      return;
    }
    try {
      await TeamService.addCollaborator({
        teamId: teamId,
        email: formik.values.email.trim().toLowerCase(),
      });
      setAdded(true);
      setTimeout(() => setAdded(false), 5000);
      setError(null);
      refetchTeam();
    } catch (error) {
      if (
        error instanceof AxiosError &&
        error.response?.data.error.details.errorCode === "USER_DOES_NOT_EXIST"
      ) {
        setError(t("invite.collaborator_does_not_exist"));
      } else if (
        error instanceof AxiosError &&
        error.response?.data.error.details.errorCode === "USER_ALREADY_IN_TEAM"
      ) {
        setError(t("invite.collaborator_already_in_team"));
      }
    }
  };

  useEffect(() => {
    if (added) {
      formik.resetForm({ values: { email: "" } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [added]);

  const formik = useForm({
    initialValues: {
      email: "",
    },
    onSubmit: onAdd,
    // onSubmit: onAdd,
    validationSchema: object().shape({
      email: string().test(testEmail).required(),
    }),
  });

  if (!isAdmin) {
    return null;
  }

  return (
    <Form formik={formik}>
      <FlexColBox gap={"12px"}>
        <Box display={"flex"} gap={"4px"} alignItems={"center"}>
          <Typography color={palette.grey.primary} fontSize={16}>
            {t("invite.add_collaborator_title")}
          </Typography>
          <AdminIcon size={16} />
        </Box>
        <Box
          display="flex"
          gap={{
            xs: spacing.regularMobile,
            lg: spacing.ctaSpacingDesktop,
          }}
          alignItems="center"
          flexDirection={{ xs: "column", lg: "row" }}
          fullWidth={isMobile}
        >
          <Input
            name="email"
            placeholder={t("invite.enter_email")}
            value={formik.values.email}
            containerSx={{
              maxWidth: "unset",
              flexGrow: 1,
              width: { xs: "100%", lg: "unset" },
            }}
          />
          <Button
            type="submit"
            size={isMobile ? "large" : "small"}
            fullWidth={isMobile}
            data-test="inviteTeammateModal.addButton"
            startIcon={
              <Icon
                icon={added ? "done" : "person_add"}
                size={24}
                color="white"
              />
            }
          >
            <Typography variant="actionBigIcon" color="white">
              {added ? t("invite.added") : t("invite.add_collaborator")}
            </Typography>
          </Button>
        </Box>
      </FlexColBox>
      {error && (
        <Message
          message={error}
          type={MessageTypes.ALERT}
          titleIcon="warning"
          sx={{
            marginTop: {
              xs: spacing.regularMobile,
              lg: spacing.regularDesktop,
            },
          }}
        />
      )}
    </Form>
  );
};

export default AddCollaboratorControls;
